import { actaApi } from './actaApi';

/**
 * Estrutura de configuração do Journal no Acta.
 */
export interface ConfigPublicacao {
  id: string;
  prefix: string;
  suffix: string;
  type: string;
  manager_name: string;
  editor_name: string;
  address: {
    street: string;
    cep: string;
  };
  dom_enabled: boolean;
  dou_enabled: boolean;
  jgc_enabled: boolean;
  emblem_filename: string;
  header: {
    year_roman_start_from: number;
    edition_number_start_from: number;
    title: string;
    subtitle: string;
  };
  created_at?: string;
  updated_at?: string;
}

export interface ConfigPublicacaoFormData {
  entity_id: string;
  prefix: string;
  suffix: string;
  type: string;
  manager_name: string;
  editor_name: string;
  address_street: string;
  address_cep: string;
  year_roman_start_from: number;
  edition_number_start_from: number;
  header_title: string;
  header_subtitle: string;
  dom_enabled: boolean;
  dou_enabled: boolean;
  jgc_enabled: boolean;
  emblem_file?: File;
}

/**
 * Lista todas as configurações de publicação.
 * GET /journal-configurations
 */
export async function listConfigPublicacoes(): Promise<ConfigPublicacao[]> {
  const response = await actaApi.get<ConfigPublicacao[]>('/journal-configurations');
  return response.data;
}

/**
 * Obtém detalhes de uma configuração
 * GET /journal-configurations/{cfgId}
 */
export async function getConfigPublicacao(cfgId: string): Promise<ConfigPublicacao> {
  const response = await actaApi.get<ConfigPublicacao>(`/journal-configurations/${cfgId}`);
  return response.data;
}

/**
 * Cria uma nova configuração de publicação (POST multipart).
 */
export async function createConfigPublicacao(payload: ConfigPublicacaoFormData) {
  const formData = new FormData();

  formData.append('cfg_id', payload.entity_id);
  formData.append('prefix', payload.prefix);
  formData.append('suffix', payload.suffix);
  formData.append('type_', payload.type);
  formData.append('manager_name', payload.manager_name);
  formData.append('editor_name', payload.editor_name);
  formData.append('address_street', payload.address_street);
  formData.append('address_cep', payload.address_cep);
  formData.append('year_roman_start_from', String(payload.year_roman_start_from));
  formData.append('edition_number_start_from', String(payload.edition_number_start_from));
  formData.append('header_title', payload.header_title);
  formData.append('header_subtitle', payload.header_subtitle);
  formData.append('dom_enabled', String(payload.dom_enabled));
  formData.append('dou_enabled', String(payload.dou_enabled));
  formData.append('jgc_enabled', String(payload.jgc_enabled));

  if (payload.emblem_file) {
    formData.append('emblem_file', payload.emblem_file, payload.emblem_file.name);
  }

  const response = await actaApi.post('/journal-configurations', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

/**
 * Atualiza uma configuração de publicação (PUT multipart).
 */
export async function updateConfigPublicacao(cfgId: string, payload: Partial<ConfigPublicacaoFormData>) {
  const formData = new FormData();

  if (payload.entity_id !== undefined) {
    formData.append('cfg_id', payload.entity_id);
  }
  if (payload.prefix !== undefined) formData.append('prefix', payload.prefix);
  if (payload.suffix !== undefined) formData.append('suffix', payload.suffix);
  if (payload.type !== undefined) formData.append('type_', payload.type);
  if (payload.manager_name !== undefined) formData.append('manager_name', payload.manager_name);
  if (payload.editor_name !== undefined) formData.append('editor_name', payload.editor_name);
  if (payload.address_street !== undefined) formData.append('address_street', payload.address_street);
  if (payload.address_cep !== undefined) formData.append('address_cep', payload.address_cep);
  if (payload.year_roman_start_from !== undefined) {
    formData.append('year_roman_start_from', String(payload.year_roman_start_from));
  }
  if (payload.edition_number_start_from !== undefined) {
    formData.append('edition_number_start_from', String(payload.edition_number_start_from));
  }
  if (payload.header_title !== undefined) formData.append('header_title', payload.header_title);
  if (payload.header_subtitle !== undefined) formData.append('header_subtitle', payload.header_subtitle);

  if (payload.dom_enabled !== undefined) formData.append('dom_enabled', String(payload.dom_enabled));
  if (payload.dou_enabled !== undefined) formData.append('dou_enabled', String(payload.dou_enabled));
  if (payload.jgc_enabled !== undefined) formData.append('jgc_enabled', String(payload.jgc_enabled));

  if (payload.emblem_file) {
    formData.append('emblem_file', payload.emblem_file, payload.emblem_file.name);
  }

  const response = await actaApi.put(`/journal-configurations/${cfgId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}
